import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ConfigService } from '../services/config.service';
import { RestService } from '../services/rest.service';
import { CommonService } from '../services/common.service';
import { GetterSetterService } from '../services/getter-setter.service';
import { PermissionService } from '../services/permission.service';
import {
  isAccessTokenExpired as accessTokenIsExpired,
  readJwtExpiryMs,
  resolveAccessTokenExpiresAtMs,
} from './access-token';
import {
  ApiUserResponse,
  AuthResponse,
  AuthUser,
  ChangePasswordRequest,
  ConfirmPasswordResetRequest,
  ForgotPasswordRequest,
  LoginRequest,
  MessageResponse,
  OtpResendRequest,
  OtpVerifyRequest,
  RefreshTokenRequest,
} from './auth.models';

const TOKEN_KEY = 'dmp_access_token';
const ACCESS_EXPIRES_AT_KEY = 'dmp_access_token_expires_at';
const REFRESH_TOKEN_KEY = 'dmp_refresh_token';
const USER_KEY = 'dmp_user';
const PERMISSIONS_KEY = 'dmp_permissions';
const PERMISSION_SCOPES_KEY = 'dmp_permission_scopes';
const MOCK_USERS_KEY = 'dmp_mock_users';
const AUTH_MODULE = 'auth';

/** Demo account for local development (mock mode only). */
const DEMO_USER = {
  email: 'demo@dmp.com',
  password: 'Password1!',
  user: {
    id: 'demo-1',
    email: 'demo@dmp.com',
    firstName: 'Wen',
    lastName: 'G.',
    role: 'Principal Operator',
    initials: 'WG',
  } satisfies AuthUser,
};

/** Fixed demo OTP accepted in mock auth mode. */
const MOCK_OTP_CODE = '123456';

interface StoredMockUser {
  email: string;
  password: string;
  user: AuthUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _currentUser = new BehaviorSubject<AuthUser | null>(this.loadStoredUser());
  readonly currentUser$ = this._currentUser.asObservable();

  /**
   * Holds login step-1 state until OTP is verified.
   * The restricted `accessToken` is also written to storage so the
   * auth interceptor can attach it to `/verify-otp` and `/resend-otp`.
   */
  private pendingAuth: { response: AuthResponse; rememberMe: boolean; email: string } | null = null;

  constructor(
    private readonly configService: ConfigService,
    private readonly restService: RestService,
    private readonly commonService: CommonService,
    private readonly getSetService: GetterSetterService,
    private readonly permissionService: PermissionService,
  ) {}

  get currentUser(): AuthUser | null {
    return this._currentUser.value;
  }

  /**
   * True only after OTP verification (or mock login without a pending challenge)
   * and only while the access token is still within its expiry.
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken()
      && !!this._currentUser.value
      && !this.isOtpPending()
      && !this.isAccessTokenExpired();
  }

  /**
   * Stored access token is present and past its expiry, or is an unreadable JWT.
   * Pending OTP challenges are left intact so the sign-in step can finish.
   */
  hasExpiredAccessToken(): boolean {
    if (this.isOtpPending()) return false;
    return this.isAccessTokenExpired();
  }

  /** Absolute expiry in epoch ms, from the JWT `exp` claim or the stored fallback. */
  getAccessTokenExpiresAt(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;
    return readJwtExpiryMs(token) ?? this.readExpiryMs();
  }

  isAccessTokenExpired(now = Date.now()): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    return accessTokenIsExpired(token, this.readExpiryMs(), now);
  }

  isOtpPending(): boolean {
    return !!this.pendingAuth;
  }

  getAccessToken(): string | null {
    return this.readStorage(TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.readStorage(REFRESH_TOKEN_KEY);
  }

  getPendingRememberMe(): boolean {
    return this.pendingAuth?.rememberMe ?? true;
  }

  // ── Login (step 1) ────────────────────────────────────────────────────────

  login(request: LoginRequest): Observable<AuthResponse> {
    if (environment.useMockAuth) {
      return this.mockLogin(request);
    }

    const trimmed = this.commonService.trimFormData({
      email: request.email,
      password: request.password,
      tenantCode: request.tenantCode,
    }) as LoginRequest;

    const body: Record<string, string> = {
      email: String(trimmed.email ?? '').trim().toLowerCase(),
      password: String(trimmed.password ?? ''),
    };
    if (trimmed.tenantCode?.trim()) {
      body['tenantCode'] = trimmed.tenantCode.trim().toLowerCase();
    }

    return this.executeAuthCall<AuthResponse>('login', body).pipe(
      tap(res => this.beginOtpChallenge(res, body['email'], request.rememberMe ?? true)),
    );
  }

  /**
   * Stores the restricted OTP-phase token from a successful login response.
   * Used by SignIn when calling via RestService instead of {@link login}.
   */
  beginOtpChallenge(
    response: AuthResponse,
    email: string,
    rememberMe = true,
  ): void {
    this.pendingAuth = {
      response,
      rememberMe,
      email: email.trim().toLowerCase(),
    };

    if (response.accessToken) {
      this.writeStorage(TOKEN_KEY, response.accessToken, false);
      this.getSetService.setOtpToken(response.accessToken);
      this.getSetService.setToken(response.accessToken);
    }
  }

  /**
   * Persists a fully authenticated session (e.g. login without OTP, or after
   * RestService verify-otp success handled outside {@link verifyOtp}).
   */
  completeLogin(response: AuthResponse, rememberMe = true): void {
    this.pendingAuth = null;
    this.persistSession(response, rememberMe);
  }

  // ── Login (step 2) — OTP ──────────────────────────────────────────────────

  /**
   * Verifies the 6-digit email OTP. Real API identifies the user from the
   * restricted JWT (Authorization header); body is `{ otp }` only.
   */
  verifyOtp(request: OtpVerifyRequest): Observable<AuthResponse> {
    if (environment.useMockAuth) {
      return this.mockVerifyOtp(request);
    }

    return this.executeAuthCall<AuthResponse>('verifyOtp', { otp: request.otp.trim() }).pipe(
      tap(res => {
        const rememberMe = this.pendingAuth?.rememberMe ?? true;
        this.pendingAuth = null;
        this.persistSession(res, rememberMe);
      }),
    );
  }

  /** Requests a new email OTP. Real API uses the restricted JWT only. */
  resendOtp(request: OtpResendRequest = {}): Observable<MessageResponse> {
    if (environment.useMockAuth) {
      return this.mockResendOtp(request);
    }

    return this.executeAuthCall<unknown>('resendOtp', {}).pipe(
      map(() => ({
        message: 'A new verification code has been sent to your email.',
      })),
    );
  }

  /** Clears any pending (pre-OTP) login without establishing a session. */
  cancelPendingLogin(): void {
    this.pendingAuth = null;
    if (!this._currentUser.value) {
      this.clearSession();
    }
  }

  // ── Password flows ────────────────────────────────────────────────────────

  forgotPassword(request: ForgotPasswordRequest): Observable<MessageResponse> {
    if (environment.useMockAuth) {
      return this.mockForgotPassword(request);
    }

    const trimmed = this.commonService.trimFormData({
      email: request.email,
      tenantCode: request.tenantCode,
    }) as ForgotPasswordRequest;

    const body: Record<string, string> = {
      email: String(trimmed.email ?? '').trim().toLowerCase(),
    };
    if (trimmed.tenantCode?.trim()) {
      body['tenantCode'] = trimmed.tenantCode.trim().toLowerCase();
    }

    return this.executeAuthCall<unknown>('requestPasswordReset', body).pipe(
      map(() => ({
        message: 'If an account exists for that email, a reset link has been sent.',
      })),
    );
  }

  confirmPasswordReset(request: ConfirmPasswordResetRequest): Observable<MessageResponse> {
    if (environment.useMockAuth) {
      return of({ message: 'Password updated successfully.' }).pipe(delay(500));
    }

    return this.executeAuthCall<unknown>('confirmPasswordReset', request).pipe(
      map(() => ({ message: 'Password updated successfully.' })),
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<MessageResponse> {
    if (environment.useMockAuth) {
      return this.mockChangePassword(request);
    }

    return this.executeAuthCall<unknown>('resetPassword', request).pipe(
      map(() => ({ message: 'Password updated successfully.' })),
    );
  }

  refreshTokens(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => ({ status: 401, error: { message: 'No refresh token.' } }));
    }

    const body: RefreshTokenRequest = { refreshToken };
    return this.executeAuthCall<AuthResponse>('refresh', body).pipe(
      tap(res => this.persistSession(res, true)),
    );
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    if (!environment.useMockAuth && refreshToken) {
      this.executeAuthCall<unknown>('logout', {
        refreshToken,
      } satisfies RefreshTokenRequest).subscribe({
        next: () => undefined,
        error: () => undefined,
      });
    }
    this.pendingAuth = null;
    this.clearSession();
    this._currentUser.next(null);
  }

  /**
   * Drops an expired or invalid session locally.
   * The logout API is not called: the access token can no longer authorize it,
   * and a 401 from that call would re-enter the error interceptor.
   */
  expireSession(): void {
    this.pendingAuth = null;
    this.clearSession();
    this._currentUser.next(null);
  }

  // ── RestService bridge ────────────────────────────────────────────────────

  private executeAuthCall<T>(callName: string, body: unknown): Observable<T> {
    const reqObj = this.configService.getRestObject(AUTH_MODULE, callName, {
      body: body ?? {},
    });
    return this.restService.execute<T>(reqObj);
  }

  // ── Mock API (development without backend) ────────────────────────────────

  private mockLogin(request: LoginRequest): Observable<AuthResponse> {
    const email = request.email.trim().toLowerCase();
    const stored = this.findMockUser(email);

    if (stored && stored.password === request.password) {
      const response = this.buildMockAuthResponse(stored.user, true);
      this.beginOtpChallenge(response, email, request.rememberMe ?? false);
      return of(response).pipe(delay(600));
    }

    if (email === DEMO_USER.email && request.password === DEMO_USER.password) {
      const response = this.buildMockAuthResponse(DEMO_USER.user, true);
      this.beginOtpChallenge(response, email, request.rememberMe ?? false);
      return of(response).pipe(delay(600));
    }

    return throwError(() => ({
      status: 401,
      error: { message: 'Invalid email or password.' },
    })).pipe(delay(600));
  }

  private mockVerifyOtp(request: OtpVerifyRequest): Observable<AuthResponse> {
    const otp = request.otp.trim();

    if (!this.pendingAuth) {
      return throwError(() => ({
        status: 401,
        error: { message: 'Your sign-in session expired. Please sign in again.' },
      })).pipe(delay(400));
    }

    if (otp !== MOCK_OTP_CODE) {
      return throwError(() => ({
        status: 400,
        error: { message: 'Invalid verification code. Please try again.' },
      })).pipe(delay(500));
    }

    const { response, rememberMe } = this.pendingAuth;
    const completed: AuthResponse = {
      ...response,
      otpRequired: false,
      refreshToken: response.refreshToken ?? `mock-refresh-${response.user?.id ?? 'user'}`,
    };
    this.pendingAuth = null;
    this.persistSession(completed, rememberMe);
    return of(completed).pipe(delay(500));
  }

  private mockResendOtp(request: OtpResendRequest): Observable<MessageResponse> {
    const email = (request.email ?? this.pendingAuth?.email ?? '').trim().toLowerCase();

    if (!this.pendingAuth || this.pendingAuth.email !== email) {
      return throwError(() => ({
        status: 401,
        error: { message: 'Your sign-in session expired. Please sign in again.' },
      })).pipe(delay(400));
    }

    return of({
      message: 'A new verification code has been sent to your email.',
    }).pipe(delay(600));
  }

  private mockForgotPassword(_request: ForgotPasswordRequest): Observable<MessageResponse> {
    return of({
      message: 'If an account exists for that email, a reset link has been sent.',
    }).pipe(delay(700));
  }

  private mockChangePassword(request: ChangePasswordRequest): Observable<MessageResponse> {
    const current = this._currentUser.value;
    if (!current) {
      return throwError(() => ({ status: 401, error: { message: 'Not authenticated.' } }));
    }

    const stored = this.findMockUser(current.email);
    const currentPassword =
      stored?.password ??
      (current.email === DEMO_USER.email ? DEMO_USER.password : null);

    if (!currentPassword || currentPassword !== request.currentPassword) {
      return throwError(() => ({
        status: 400,
        error: { message: 'Current password is incorrect.' },
      })).pipe(delay(600));
    }

    if (stored) {
      stored.password = request.newPassword;
      this.saveMockUser(stored);
    }

    return of({ message: 'Password updated successfully.' }).pipe(delay(700));
  }

  private findMockUser(email: string): StoredMockUser | undefined {
    return this.loadMockUsers().find(u => u.email === email.trim().toLowerCase());
  }

  private loadMockUsers(): StoredMockUser[] {
    const raw = this.readStorage(MOCK_USERS_KEY);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as StoredMockUser[];
    } catch {
      return [];
    }
  }

  private saveMockUser(entry: StoredMockUser): void {
    const users = this.loadMockUsers().filter(u => u.email !== entry.email);
    users.push(entry);
    this.writeStorage(MOCK_USERS_KEY, JSON.stringify(users), true);
  }

  private buildMockAuthResponse(user: AuthUser, otpRequired: boolean): AuthResponse {
    return {
      accessToken: `mock-token-${user.id}-${Date.now()}`,
      refreshToken: otpRequired ? null : `mock-refresh-${user.id}`,
      accessTokenExpiresInSeconds: 3600,
      otpRequired,
      user,
      permissions: [],
      permissionScopes: {},
    };
  }

  private buildInitials(firstName: string, lastName: string): string {
    const a = (firstName ?? '').trim().charAt(0).toUpperCase();
    const b = (lastName ?? '').trim().charAt(0).toUpperCase();
    return `${a}${b}` || 'U';
  }

  /**
   * Keeps the full API `user` payload and overlays UI-derived fields.
   * Do not cherry-pick a subset — extra/future API fields must round-trip into storage.
   */
  private mapApiUser(apiUser: ApiUserResponse | AuthUser, permissions?: string[]): AuthUser {
    const raw = { ...(apiUser as Record<string, unknown>) };
    const firstName = String(raw['firstName'] ?? '').trim();
    const lastName = String(raw['lastName'] ?? '').trim();
    const roleNames = this.normalizeRoleNames(raw['roleNames']);
    const platformAdmin = !!raw['platformAdmin'];
    const existingRole = typeof raw['role'] === 'string' ? raw['role'].trim() : '';
    const existingInitials = typeof raw['initials'] === 'string' ? raw['initials'].trim() : '';
    const tenantNameRaw = raw['tenantName'];
    const tenantName =
      typeof tenantNameRaw === 'string' ? tenantNameRaw.trim() || null : (tenantNameRaw as string | null | undefined);

    const role =
      existingRole ||
      (roleNames.length ? roleNames.join(', ') : '') ||
      (platformAdmin ? 'Platform Admin' : 'User');

    return {
      ...raw,
      id: String(raw['id'] ?? ''),
      email: String(raw['email'] ?? ''),
      firstName,
      lastName,
      tenantId: raw['tenantId'] != null && raw['tenantId'] !== '' ? String(raw['tenantId']) : undefined,
      tenantName,
      roleNames,
      permissions: permissions ?? (Array.isArray(raw['permissions']) ? (raw['permissions'] as string[]) : undefined),
      role,
      initials: existingInitials || this.buildInitials(firstName, lastName),
    };
  }

  private normalizeRoleNames(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map(name => (typeof name === 'string' ? name.trim() : ''))
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(name => name.trim())
        .filter(Boolean);
    }
    return [];
  }

  // ── Session persistence ───────────────────────────────────────────────────

  private persistSession(response: AuthResponse, rememberMe: boolean): void {
    const persistent = rememberMe ?? true;
    const apiUser = response.user;
    if (!apiUser) {
      throw new Error('Auth response did not include a user profile.');
    }

    const user = this.mapApiUser(apiUser, response.permissions);

    this.writeStorage(TOKEN_KEY, response.accessToken, persistent);
    const expiresAt = resolveAccessTokenExpiresAtMs(response.accessToken, response.accessTokenExpiresInSeconds);
    if (expiresAt == null) {
      this.removeStorage(ACCESS_EXPIRES_AT_KEY);
    } else {
      this.writeStorage(ACCESS_EXPIRES_AT_KEY, String(expiresAt), persistent);
    }
    if (response.refreshToken) {
      this.writeStorage(REFRESH_TOKEN_KEY, response.refreshToken, persistent);
    } else {
      this.removeStorage(REFRESH_TOKEN_KEY);
    }
    this.writeStorage(USER_KEY, JSON.stringify(user), persistent);
    this.writeStorage(PERMISSIONS_KEY, JSON.stringify(response.permissions ?? []), persistent);
    this.writeStorage(
      PERMISSION_SCOPES_KEY,
      JSON.stringify(response.permissionScopes ?? {}),
      persistent,
    );
    this.permissionService.setPermissions(response.permissions ?? []);
    this.permissionService.setPermissionScopes(response.permissionScopes ?? {});

    this.getSetService.setToken(response.accessToken);
    // Persist the full mapped user (all API fields + UI helpers) for layout/session consumers.
    this.getSetService.setUserDetails({
      ...user,
      token: response.accessToken,
      permissions: response.permissions ?? user.permissions ?? [],
      permissionScopes: response.permissionScopes ?? {},
    });
    this.getSetService.setLoggedInUser(user);
    localStorage.removeItem('otptoken');

    this._currentUser.next(user);
  }

  private loadStoredUser(): AuthUser | null {
    const token = this.getAccessToken();
    const raw = this.readStorage(USER_KEY);
    if (!token || !raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }

  private clearSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.removeStorage(TOKEN_KEY);
    this.removeStorage(ACCESS_EXPIRES_AT_KEY);
    this.removeStorage(USER_KEY);
    this.removeStorage(REFRESH_TOKEN_KEY);
    this.removeStorage(PERMISSIONS_KEY);
    this.removeStorage(PERMISSION_SCOPES_KEY);
    this.permissionService.clear();
    this.getSetService.logout();
  }

  private removeStorage(key: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }

  private readExpiryMs(): number | null {
    const raw = this.readStorage(ACCESS_EXPIRES_AT_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  private readStorage(key: string): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  }

  private writeStorage(key: string, value: string, persistent: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
    const store = persistent ? localStorage : sessionStorage;
    store.setItem(key, value);
  }
}
